import { VolumeUpIcon } from '@heroicons/react/outline';
import { memo, useEffect, useRef } from 'react';
import { SpeakerSegment } from 'types';

import clsxm from '@/lib/clsxm';
import { convertToHumanReadableSeconds } from '@/lib/convertTranscriptionTimestamp';

import { useAudioPlayer } from '@/contexts/AudioProvider';

interface TranscriptionWithSpeakersSegmentProps {
  segment: SpeakerSegment;
  speakerKey: string;
}

export const TranscriptionWithSpeakersSegment = ({
  segment,
  speakerKey,
}: TranscriptionWithSpeakersSegmentProps) => {
  const player: any = useAudioPlayer();

  // if player currentTime is within the segment then scroll to the segment
  const segmentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      player.currentTime >= segment.start &&
      player.currentTime <= segment.end
    ) {
      segmentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [player.currentTime, segment.start, segment.end]);

  const words = segment.text.split(' ');
  // @ts-ignore
  const totalDuration = Number(segment.end) - Number(segment.start);
  const durationPerWord = totalDuration / words.length;
  const isSegmentPlaying =
    player.currentTime >= segment.start && player.currentTime <= segment.end;

  return (
    <div
      className='flex flex-col py-2 md:flex-row md:space-x-4'
      ref={segmentRef}
    >
      <div className='w-full md:w-1/4'>
        <span className='flex items-center justify-start font-bold dark:text-slate-400'>
          {speakerKey}{' '}
          <a
            className='ml-1 flex cursor-pointer items-center text-gray-400 dark:text-slate-600'
            onClick={() => {
              player.seek(segment.start);
              player.play();
            }}
          >
            <VolumeUpIcon className='h-5 w-5 text-gray-400 hover:text-primary-500 dark:text-slate-600' />
          </a>
        </span>
        <span className='text-xs text-gray-400 dark:text-slate-600'>
          {convertToHumanReadableSeconds(segment.start)} -{' '}
          {convertToHumanReadableSeconds(segment.end)}
        </span>
      </div>
      <div className='mt-2 w-full md:mt-0 '>
        <span
          className={clsxm(isSegmentPlaying ? '' : '', `dark:text-slate-400 `)}
        >
          {isSegmentPlaying ? (
            <>
              {segment.words
                ? segment.words.map((wordObj, index) => {
                    const isCurrentWord =
                      player.playing &&
                      player.currentTime >= wordObj.start - 0.01 &&
                      player.currentTime <= wordObj.end + 0.01;

                    return (
                      <span
                        key={index}
                        className={
                          isCurrentWord
                            ? 'rounded bg-primary-600 py-[1px] text-white dark:bg-primary-600'
                            : ''
                        }
                      >
                        {wordObj.word}{' '}
                      </span>
                    );
                  })
                : words.map((word, index) => {
                    const isCurrentWord =
                      player.playing &&
                      player.currentTime >=
                        Number(segment.start) + (index - 1) * durationPerWord &&
                      player.currentTime <=
                        Number(segment.start) + (index + 1) * durationPerWord;

                    return (
                      <span
                        key={index}
                        className={
                          isCurrentWord
                            ? 'rounded bg-primary-600 py-[1px] text-white dark:bg-primary-600'
                            : ''
                        }
                      >
                        {word}{' '}
                      </span>
                    );
                  })}
            </>
          ) : (
            <span>{segment.text}</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default memo(TranscriptionWithSpeakersSegment);
